import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";

const fmt = (n, dp = 2) =>
  Number.isFinite(+n) ? Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }) : "—";

export default function Rates() {
  const { t, user } = useApp();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    sar_to_idr_buy: "",
    sar_to_idr_sell: "",
    idr_to_usdt_buy: "",
    idr_to_usdt_sell: "",
    usdt_to_sar_buy: "",
    usdt_to_sar_sell: "",
    sar_to_usdt_buy: "",
    sar_to_usdt_sell: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, h] = await Promise.all([
      api.get("/rates/current"),
      api.get("/rates/history"),
    ]);
    setCurrent(c.data);
    setHistory(h.data);
    if (c.data) {
      setForm({
        sar_to_idr_buy: c.data.sar_to_idr_buy ?? "",
        sar_to_idr_sell: c.data.sar_to_idr_sell ?? "",
        idr_to_usdt_buy: c.data.idr_to_usdt_buy ?? "",
        idr_to_usdt_sell: c.data.idr_to_usdt_sell ?? "",
        usdt_to_sar_buy: c.data.usdt_to_sar_buy ?? "",
        usdt_to_sar_sell: c.data.usdt_to_sar_sell ?? "",
        sar_to_usdt_buy: c.data.sar_to_usdt_buy ?? "",
        sar_to_usdt_sell: c.data.sar_to_usdt_sell ?? "",
        note: "",
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, k === "note" ? v : +v || 0])
      );
      await api.post("/rates", payload);
      toast.success(t("rate_saved"));
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const isAdmin = user?.role === "admin";

  const pairs = [
    {
      key: "sar_idr",
      badges: [
        { c: "SAR", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
        { c: "IDR", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30" },
      ],
      unit: "IDR / SAR",
      buyKey: "sar_to_idr_buy",
      sellKey: "sar_to_idr_sell",
      testidBuy: "rate-sar-buy",
      testidSell: "rate-sar-sell",
    },
    {
      key: "idr_usdt",
      badges: [
        { c: "IDR", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30" },
        { c: "USDT", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
      ],
      unit: "IDR / USDT",
      buyKey: "idr_to_usdt_buy",
      sellKey: "idr_to_usdt_sell",
      testidBuy: "rate-usdt-buy",
      testidSell: "rate-usdt-sell",
    },
    {
      key: "usdt_sar",
      badges: [
        { c: "USDT", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
        { c: "SAR", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
      ],
      unit: "SAR / USDT",
      buyKey: "usdt_to_sar_buy",
      sellKey: "usdt_to_sar_sell",
      testidBuy: "rate-usdt-sar-buy",
      testidSell: "rate-usdt-sar-sell",
    },
    {
      key: "sar_usdt",
      badges: [
        { c: "SAR", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
        { c: "USDT", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
      ],
      unit: "USDT / SAR",
      buyKey: "sar_to_usdt_buy",
      sellKey: "sar_to_usdt_sell",
      testidBuy: "rate-sar-usdt-buy",
      testidSell: "rate-sar-usdt-sell",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t("rates")}</h1>
        {current && (
          <span className="text-xs text-slate-500 font-mono">
            Last update: {new Date(current.created_at).toLocaleString()}
          </span>
        )}
      </div>

      <Card className="p-5 glass border-slate-800">
        <h3 className="font-semibold mb-4">{t("manual_rate")}</h3>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pairs.map((p) => (
            <div key={p.key} className="rounded-lg border border-slate-800 p-4 bg-slate-900/40">
              <div className="flex items-center gap-2 mb-1">
                <span className={`pill border ${p.badges[0].cls}`}>{p.badges[0].c}</span>
                <span className="text-slate-500">→</span>
                <span className={`pill border ${p.badges[1].cls}`}>{p.badges[1].c}</span>
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-3">{p.unit}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("buying")}</Label>
                  <Input
                    data-testid={p.testidBuy}
                    type="number"
                    step="0.0001"
                    disabled={!isAdmin}
                    value={form[p.buyKey]}
                    onChange={(e) => setForm({ ...form, [p.buyKey]: e.target.value })}
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("selling")}</Label>
                  <Input
                    data-testid={p.testidSell}
                    type="number"
                    step="0.0001"
                    disabled={!isAdmin}
                    value={form[p.sellKey]}
                    onChange={(e) => setForm({ ...form, [p.sellKey]: e.target.value })}
                    className="mt-1 font-mono"
                  />
                </div>
              </div>
              {form[p.buyKey] && form[p.sellKey] && (
                <div className="mt-3 text-xs text-slate-500 flex justify-between">
                  <span>Spread</span>
                  <span className="font-mono text-amber-400">
                    {fmt(+form[p.sellKey] - +form[p.buyKey], 4)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
        {isAdmin && (
          <>
            <div className="mt-4">
              <Label>{t("notes")}</Label>
              <Textarea
                data-testid="rate-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={2}
                className="mt-1.5"
              />
            </div>
            <Button
              data-testid="rate-save"
              onClick={save}
              disabled={busy}
              className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            >
              {t("save_rate")}
            </Button>
          </>
        )}
      </Card>

      <Card className="p-5 glass border-slate-800">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-amber-400" />
          <h3 className="font-semibold">Rate History</h3>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">No rate updates yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead className="text-left text-slate-500 uppercase text-[10px]">
                <tr className="border-b border-slate-800">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">SAR→IDR B/S</th>
                  <th className="py-2 pr-3">IDR→USDT B/S</th>
                  <th className="py-2 pr-3">USDT→SAR B/S</th>
                  <th className="py-2 pr-3">SAR→USDT B/S</th>
                  <th className="py-2 pr-3">By</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 20).map((h) => (
                  <tr key={h.id} className="border-b border-slate-800/60">
                    <td className="py-2 pr-3 text-slate-500">{new Date(h.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-amber-400">
                      {fmt(h.sar_to_idr_buy, 2)} / {fmt(h.sar_to_idr_sell, 2)}
                    </td>
                    <td className="py-2 pr-3 text-emerald-400">
                      {fmt(h.idr_to_usdt_buy, 2)} / {fmt(h.idr_to_usdt_sell, 2)}
                    </td>
                    <td className="py-2 pr-3 text-sky-400">
                      {fmt(h.usdt_to_sar_buy || 0, 4)} / {fmt(h.usdt_to_sar_sell || 0, 4)}
                    </td>
                    <td className="py-2 pr-3 text-violet-400">
                      {fmt(h.sar_to_usdt_buy || 0, 6)} / {fmt(h.sar_to_usdt_sell || 0, 6)}
                    </td>
                    <td className="py-2 pr-3 text-slate-500">{h.created_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
